import React, { useState, useEffect, useMemo } from 'react';
import { MasterFlightRecord, formatDisplayDate, getDisplayDate, getRouteString } from '../../../utils/types';
import { SectionHeader } from '../common/SectionHeader';
import { computeComparison } from './variantComparison';
import { useTimezone } from '../../../contexts/TimezoneContext';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface VariantComparisonModalProps {
  open: boolean;
  variants: MasterFlightRecord[];
  onClose: () => void;
}

const COMPARE_MAX = 3;

export function VariantComparisonModal({ open, variants, onClose }: VariantComparisonModalProps) {
  const { timezone } = useTimezone();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [step, setStep] = useState<1 | 2>(1);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setStep(1);
    }
  }, [open]);

  const sortedVariants = useMemo(
    () => [...variants].sort((a, b) => a.itineraryVarId - b.itineraryVarId),
    [variants],
  );

  const toggleId = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  };

  const comparisonData = useMemo(() => {
    if (selectedIds.length < 2) return null;
    const selected = selectedIds
      .map(id => variants.find(v => v._id === id))
      .filter((v): v is MasterFlightRecord => v != null);
    if (selected.length < 2) return null;
    return { variants: selected, ...computeComparison(selected, timezone) };
  }, [selectedIds, variants, timezone]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 z-[200] animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-h-[85vh] bg-white rounded-2xl shadow-2xl animate-slide-up compare-modal flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 rounded-t-2xl border-b border-gray-200">
          <div className="flex items-center gap-3">
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                className="p-1 rounded hover:bg-gray-200 text-gray-500 transition-colors"
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </button>
            )}
            <div>
              <h3 className="text-lg font-bold text-gray-900 m-0">
                {step === 1 ? 'Select Itineraries' : 'Compare Itineraries'}
              </h3>
              <p className="text-xs text-gray-500 m-0">
                Step {step} of 2
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-200 text-gray-500 transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 min-h-0 px-6 py-5">
          {step === 1 ? (
            <>
              <p className="text-xs text-gray-500 mb-3">Select 2–3 itineraries to compare</p>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-10"></th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Var</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Season</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Route</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Start</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">End</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedVariants.map(v => {
                      const id = v._id || '';
                      const isChecked = selectedIds.includes(id);
                      const isDisabled = !isChecked && selectedIds.length >= COMPARE_MAX;

                      return (
                        <tr
                          key={id}
                          className={`transition-colors ${isChecked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={isDisabled}
                              onChange={() => toggleId(id)}
                              className="w-3.5 h-3.5 rounded border-gray-300 text-primary-500 focus:ring-primary-500 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-3 py-2 text-center text-xs font-semibold text-gray-800">
                            {v.itineraryVarId}
                          </td>
                          <td className="px-3 py-2 text-center text-xs text-gray-600">
                            {v.seasonId}
                          </td>
                          <td className="px-3 py-2 text-center text-xs text-gray-600">
                            {v.scheduleStatus || '—'}
                          </td>
                          <td className="px-3 py-2 text-center text-xs text-gray-600">
                            {getRouteString(v.stations)}
                          </td>
                          <td className="px-3 py-2 text-center text-xs text-gray-600">
                            {formatDisplayDate(getDisplayDate(v.startDate, v.stations?.[0]?.std, timezone))}
                          </td>
                          <td className="px-3 py-2 text-center text-xs text-gray-600">
                            {formatDisplayDate(getDisplayDate(v.endDate, v.stations?.[0]?.std, timezone))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            comparisonData && (
              <>
                <div className="mb-3">
                  <SectionHeader title={`Comparison (${comparisonData.diffCount} difference${comparisonData.diffCount !== 1 ? 's' : ''})`} />
                </div>

                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="max-h-[400px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-40">
                            Field
                          </th>
                          {comparisonData.variants.map(v => (
                            <th key={v._id} className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Var {v.itineraryVarId} ({v.seasonId})
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {comparisonData.rows.map(row => (
                          <tr
                            key={row.fieldKey}
                            className={row.hasDiff ? 'bg-amber-50' : ''}
                          >
                            <td className="px-3 py-1.5 text-xs font-medium text-gray-600 whitespace-nowrap">
                              <span className="flex items-center gap-1.5">
                                {row.hasDiff && (
                                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                                )}
                                {row.label}
                              </span>
                            </td>
                            {row.values.map((val, i) => (
                              <td
                                key={i}
                                className={`px-3 py-1.5 text-center text-xs ${row.mono ? 'font-mono' : ''} ${
                                  row.hasDiff ? 'font-medium text-gray-900' : 'text-gray-600'
                                }`}
                              >
                                {val}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <p className="mt-2 text-xs text-gray-400">
                  {comparisonData.diffCount} difference{comparisonData.diffCount !== 1 ? 's' : ''} found across {comparisonData.rows.length} fields
                </p>
              </>
            )
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50 rounded-b-2xl border-t border-gray-200">
          <span className="text-xs text-gray-500">
            {step === 1
              ? `${selectedIds.length} of ${COMPARE_MAX} selected`
              : `Comparing ${comparisonData?.variants.length || 0} itineraries`}
          </span>
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            )}
            {step === 1 ? (
              <button
                disabled={selectedIds.length < 2}
                onClick={() => setStep(2)}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
